import { useState, useEffect, useRef } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';
import Game from './components/Game';
import {
  Backdrop,
  Button,
  CRTOverlay,
  GlitchText,
  JuiceLayer,
  Loader,
  Panel,
} from './ui';
import './App.css';

const MAX_AUTHING_RETRIES = 3;
const SDK_STEP_TIMEOUT_MS = 15000;

// sdk.ready()/authorize()/authenticate() are RPC calls to the Discord host,
// not HTTP requests - if the host stops responding mid-call the promise
// never resolves or rejects on its own, so without a timeout the UI would
// be stuck on the loading spinner forever with no way to recover.
function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Timed out waiting for ${label}`)), SDK_STEP_TIMEOUT_MS)
    ),
  ]);
}

function App() {
  const [discordSdk, setDiscordSdk] = useState(null);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // Prevent double-running the same attempt in React StrictMode (which
  // invokes effects twice in dev), while still allowing a genuine retry
  // (a new `attempt` value) to run.
  const startedAttempt = useRef(-1);

  useEffect(() => {
    if (startedAttempt.current === attempt) return;
    startedAttempt.current = attempt;

    // React.StrictMode double-invokes effects on mount (mount -> cleanup ->
    // mount) to surface missing-cleanup bugs. That phantom cleanup must not
    // cancel the real, still-running setup below - so staleness is checked
    // against the ref above, which only changes on a genuine new attempt
    // (see handleRetry), not on StrictMode's synchronous double-invoke.
    const isStale = () => startedAttempt.current !== attempt;

    // Dev-only escape hatch: `?nodiscord=1` skips the SDK handshake entirely so
    // the UI can be worked on in a normal browser with HMR, instead of
    // relaunching the activity in Discord for every style change. The game runs
    // fully; only rich presence and win-posting (which need the SDK and an
    // interaction token) are inert. import.meta.env.DEV strips this from
    // production builds, same as the reset button in Game.
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('nodiscord')) {
      console.log('[App] ?nodiscord — skipping Discord SDK setup');
      setLoading(false);
      return;
    }

    const setupDiscord = async (retryCount = 0) => {
      try {
        const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;

        if (!clientId) {
          throw new Error('VITE_DISCORD_CLIENT_ID is not set');
        }

        console.log('Initializing Discord SDK...');
        const sdk = new DiscordSDK(clientId);
        await withTimeout(sdk.ready(), 'sdk.ready()');
        console.log('Discord SDK ready');

        if (isStale()) return;
        setDiscordSdk(sdk);

        console.log('Starting authorization...');
        const { code } = await withTimeout(
          sdk.commands.authorize({
            client_id: clientId,
            response_type: 'code',
            state: '',
            prompt: 'none',
            // rpc.activities.write is required by Game.jsx's setActivity()
            // call on win (rich presence update) - without it Discord
            // returns a silent "Not authenticated or invalid scope" error.
            scope: ['identify', 'guilds', 'rpc.activities.write'],
          }),
          'sdk.commands.authorize()'
        );

        console.log('Authorization successful, exchanging code for token...');

        const tokenResponse = await fetch('/api/discord/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        if (!tokenResponse.ok) {
          throw new Error('Failed to exchange authorization code for a token');
        }

        const { access_token } = await tokenResponse.json();
        const authResult = await withTimeout(sdk.commands.authenticate({ access_token }), 'sdk.commands.authenticate()');

        if (!authResult) {
          throw new Error('Authenticate command failed');
        }

        if (isStale()) return;
        console.log('Authentication successful');
        setAuth(authResult);
        setLoading(false);
      } catch (err) {
        if (isStale()) return;
        console.error('Error setting up Discord SDK:', err);

        // A prior authorize() attempt (e.g. interrupted by a page reload)
        // can still be in-flight on Discord's side. Retry a few times
        // instead of leaving the UI stuck on the loading spinner forever.
        if (err?.message?.includes('Already authing') && retryCount < MAX_AUTHING_RETRIES) {
          console.log(`Authorization already in progress, retrying (${retryCount + 1}/${MAX_AUTHING_RETRIES})...`);
          setTimeout(() => {
            if (!isStale()) setupDiscord(retryCount + 1);
          }, 1000);
          return;
        }

        setError(err?.message);
        setLoading(false);
      }
    };

    setupDiscord();
  }, [attempt]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setDiscordSdk(null);
    setAuth(null);
    setAttempt((prev) => prev + 1);
  };

  // The atmosphere layers are mounted once, outside every branch, so the
  // backdrop and tube never restart when the app moves between loading, error
  // and playing states.
  return (
    <>
      <Backdrop />

      <div className="app">
        {loading && <Loader message="Booting Decrypedia" hint="Handshaking with Discord…" />}

        {!loading && error && (
          <div className="app__fault">
            <GlitchText as="h2" className="app__fault-title" intensity="extreme" tone="red">
              SIGNAL LOST
            </GlitchText>

            <Panel variant="hot" label="Error" className="app__fault-panel">
              <p className="app__fault-message">{error}</p>
              <p className="app__fault-hint">
                Check that the Discord application is set up correctly and that
                VITE_DISCORD_CLIENT_ID is present in your .env file.
              </p>
            </Panel>

            <Button variant="primary" size="lg" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && <Game discordSdk={discordSdk} auth={auth} />}
      </div>

      <JuiceLayer />
      <CRTOverlay />
    </>
  );
}

export default App;
